// Putting dictated words where the caret was, as if they'd been typed there.
import { UNSPACED } from './passage';
import { readsAsEnglish, type WordList } from './word-completion';

// The writing before starts a sentence: there's none, or it ends one, or a line.
const SENTENCE_START = /(?:^|[.!?…。！？]["'”’»)\]]*)\s*$|\n[^\S\n]*$/u;
// What follows carries on the sentence: a word in lower case, a number, or punctuation.
const CARRIES_ON = /^\s*[\p{Ll}\p{N},;:.!?…)\]]/u;

// A space between two characters, unless one is a space already, or punctuation that keeps to
// its word, or in writing without spaces.
const spaceBetween = (left: string | undefined, right: string | undefined) =>
  left !== undefined &&
  right !== undefined &&
  !/\s|[([{]/u.test(left) &&
  !/\s|[,.;:!?…)\]}]/u.test(right) &&
  !UNSPACED.test(left) &&
  !UNSPACED.test(right)
    ? ' '
    : '';

/**
 * The draft with a transcript in place of `start` to `end`, the selection or caret dictation
 * started at, and the caret after it. It's fitted in: spaced from the words on either side, and
 * where it carries on a sentence, without the full stop transcripts end with, and in English,
 * without the capital they start with, unless it's "I" or a word the list doesn't have, such
 * as a name.
 */
export function insertTranscript(
  text: string,
  transcript: string,
  { start, end }: { readonly start: number; readonly end: number },
  words: WordList | null = null,
): { text: string; caret: number } {
  let piece = transcript.trim();
  if (!piece) return { text, caret: end };
  const before = text.slice(0, start);
  const after = text.slice(end);
  if (CARRIES_ON.test(after)) piece = piece.replace(/(?<!\.)\.$/u, '');
  const first = /^\p{Lu}\p{Ll}*(?![\p{L}\p{M}])/u.exec(piece)?.[0];
  if (
    first &&
    first !== 'I' &&
    words &&
    !SENTENCE_START.test(before) &&
    words.rank.has(first.toLowerCase()) &&
    readsAsEnglish(words, `${before} ${piece} `) === true
  ) {
    piece = first.toLowerCase() + piece.slice(first.length);
  }
  const head = before + spaceBetween(before.at(-1), piece[0]) + piece;
  return { text: head + spaceBetween(piece.at(-1), after[0]) + after, caret: head.length };
}
